<script setup>
import { nextTick, onMounted, shallowRef, useTemplateRef } from 'vue'
import ClassicAuthCard from '../components/ClassicAuthCard.vue'

const props = defineProps({
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  clearOnReload: { type: Boolean, default: false },
})

const emit = defineEmits(['submitCredentials', 'submitPhone', 'clearError'])
const mode = shallowRef('email')
const email = shallowRef('')
const password = shallowRef('')
const phone = shallowRef('')
const passwordVisible = shallowRef(false)
const emailInput = useTemplateRef('emailInput')
const passwordInput = useTemplateRef('passwordInput')
const phoneInput = useTemplateRef('phoneInput')

onMounted(() => {
  if (!props.clearOnReload) return

  nextTick(() => {
    email.value = ''
    password.value = ''
    phone.value = ''

    if (emailInput.value) emailInput.value.value = ''
    if (passwordInput.value) passwordInput.value.value = ''
    if (phoneInput.value) phoneInput.value.value = ''
  })
})

function submitEmail() {
  if (props.loading || !email.value.trim() || !password.value) return
  emit('submitCredentials', { identifier: email.value.trim(), password: password.value })
}

function submitPhone() {
  if (props.loading || !phone.value.trim()) return
  emit('submitPhone', phone.value.trim())
}

function switchMode(nextMode) {
  mode.value = nextMode
  emit('clearError')
}
</script>

<template>
  <ClassicAuthCard title="Admin Login" is-admin>
    <form
      v-if="mode === 'email'"
      class="classic-admin"
      :autocomplete="clearOnReload ? 'off' : 'on'"
      @submit.prevent="submitEmail"
    >
      <label class="classic-admin__label" for="classic-admin-email">
        <img src="/images/admin/email.png" alt="">
        Email *
      </label>
      <input
        id="classic-admin-email"
        ref="emailInput"
        v-model="email"
        class="classic-admin__input"
        name="email"
        type="email"
        :autocomplete="clearOnReload ? 'off' : 'username'"
        required
        @input="emit('clearError')"
      >

      <label class="classic-admin__label classic-admin__label--password" for="classic-admin-password">
        <img src="/images/admin/password.png" alt="">
        Password *
      </label>
      <div class="classic-admin__password">
        <input
          id="classic-admin-password"
          ref="passwordInput"
          v-model="password"
          class="classic-admin__input"
          name="password"
          :type="passwordVisible ? 'text' : 'password'"
          :autocomplete="clearOnReload ? 'new-password' : 'current-password'"
          required
          @input="emit('clearError')"
        >
        <button
          class="classic-admin__password-toggle"
          type="button"
          :aria-label="passwordVisible ? 'Hide password' : 'Show password'"
          @click="passwordVisible = !passwordVisible"
        >
          <img class="auth-login__classic-password-eye" src="/images/icons/eye.png" alt="">
        </button>
      </div>

      <p v-if="error" class="classic-admin__error" role="alert">{{ error }}</p>
      <button class="classic-admin__switch" type="button" @click="switchMode('phone')">
        Login with mobile number
      </button>
      <button class="classic-admin__submit" type="submit" :disabled="loading || !email || !password">
        {{ loading ? 'Please wait…' : 'Login' }}
      </button>
    </form>

    <form
      v-else
      class="classic-admin"
      :autocomplete="clearOnReload ? 'off' : 'on'"
      @submit.prevent="submitPhone"
    >
      <label class="classic-admin__label" for="classic-admin-phone">Mobile Phone *</label>
      <input
        id="classic-admin-phone"
        ref="phoneInput"
        v-model="phone"
        class="classic-admin__input"
        name="phone_number"
        type="tel"
        :autocomplete="clearOnReload ? 'off' : 'tel'"
        required
        @input="emit('clearError')"
      >
      <p v-if="error" class="classic-admin__error" role="alert">{{ error }}</p>
      <button class="classic-admin__switch" type="button" @click="switchMode('email')">
        Login with email
      </button>
      <button class="classic-admin__submit" type="submit" :disabled="loading || !phone">
        {{ loading ? 'Please wait…' : 'Sent code' }}
      </button>
    </form>
  </ClassicAuthCard>
</template>

<style scoped>
.classic-admin {
  display: flex;
  flex-direction: column;
}

.classic-admin__label {
  align-items: center;
  color: #333;
  display: flex;
  font-size: 14px;
  font-weight: 600;
  gap: 8px;
  margin-bottom: 8px;
}

.classic-admin__label--password {
  margin-top: 16px;
}

.classic-admin__label img {
  height: 20px;
  object-fit: contain;
  width: 20px;
}

.classic-admin__input {
  background: transparent;
  border: 0;
  border-bottom: 1px solid #c6c6c6;
  border-radius: 0;
  box-sizing: border-box;
  color: #7c7c7c;
  font: inherit;
  min-height: 46px;
  padding: 10px 0;
  width: 100%;
}

.classic-admin__input::placeholder {
  color: #7c7c7c;
  opacity: 0.75;
}

.classic-admin__input:focus {
  border-color: #56dbcb;
  box-shadow: none;
  outline: none;
}

.classic-admin__password {
  position: relative;
}

.classic-admin__password .classic-admin__input {
  padding-right: 34px;
}

.classic-admin__password-toggle {
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 4px;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.classic-admin__error {
  color: #dc3545;
  font-size: 13px;
  margin: 8px 0 0;
}

.classic-admin__switch {
  align-self: flex-end;
  background: none;
  border: 0;
  color: #564495;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  margin: 15px 0;
  padding: 0;
}

.classic-admin__submit {
  align-self: center;
  background: #56dbcb;
  border: 0;
  border-radius: 25px;
  color: #fff;
  cursor: pointer;
  font: inherit;
  font-weight: 500;
  min-width: 230px;
  padding: 12px 20px;
}

.classic-admin__submit:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
